import React, { 
    PureComponent
} from 'react';

import {
    Typography
} from "@mui/material"

import { 
    LineChart, 
    ReferenceLine, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    Legend, 
    ResponsiveContainer 
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) {
        return null;
    }

    const getValue = (dataKey) => {
        const item = payload.find(item => item.dataKey === dataKey);
        return Number(item?.value || 0);
    };

    const ativos = getValue('Ativo');
    const receptivos = getValue('Receptivo');

    const items = [
        {
            label: 'Total',
            value: ativos + receptivos,
            color: '#343A40',
        },
        {
            label: 'Ativo',
            value: ativos,
            color: '#1976D2',
        },
        {
            label: 'Receptivo',
            value: receptivos,
            color: '#2E7D32',
        },
        {
            label: 'Serviço',
            value: getValue('Serviço'),
            color: '#00A896',
        },
        {
            label: 'Marketing',
            value: getValue('Marketing'),
            color: '#D32F2F',
        },
        {
            label: 'Autenticação',
            value: getValue('Autenticação'),
            color: '#7B1FA2',
        },
        {
            label: 'Utilidade',
            value: getValue('Utilidade'),
            color: '#EF6C00',
        },
    ];

    return (
        <div
            style={{
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '12px 16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                minWidth: '210px',
            }}
        >
            {/* Month / Year */}
            <div
                style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#343A40',
                }}
            >
                {label}
            </div>

            {/* Atendimentos section */}
            <div
                style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#343A40',
                    marginBottom: '8px',
                }}
            >
                Atendimentos
            </div>

            {items.slice(0, 3).map((item) => (
                <div
                    key={item.label}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '20px',
                        marginBottom: '5px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '7px',
                        }}
                    >
                        <span
                            style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: item.color,
                            }}
                        />

                        <span>{item.label}:</span>
                    </div>

                    <strong>{item.value}</strong>
                </div>
            ))}

            {/* Separator */}
            <div
                style={{
                    borderTop: '1px solid #E2E2E2',
                    margin: '10px 0',
                }}
            />

            {/* Messages section */}
            <div
                style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#343A40',
                    marginBottom: '8px',
                }}
            >
                Mensagens Enviadas
            </div>

            {items.slice(3).map((item) => (
                <div
                    key={item.label}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '20px',
                        marginBottom: '5px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '7px',
                        }}
                    >
                        <span
                            style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: item.color,
                            }}
                        />

                        <span>{item.label}:</span>
                    </div>

                    <strong>{item.value}</strong>
                </div>
            ))}
        </div>
    );
};

const CustomLegend = ({ payload }) => {
    const atendimentoItems = payload.filter(item =>
        ['Total', 'Ativo', 'Receptivo'].includes(item.value)
    );

    const messageItems = payload.filter(item =>
        ['Serviço', 'Marketing', 'Utilidade', 'Autenticação'].includes(item.value)
    );

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'stretch',
                width: '100%',
                padding: '20px 20px 0',
            }}
        >
            {/* Atendimentos */}
            <div
                style={{
                    paddingRight: '30px',
                    textAlign: 'center',
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{ marginBottom: '.5rem', textAlign: 'left' }}
                >
                    Atendimentos
                </Typography>

                <div
                    style={{
                        display: 'flex',
                        gap: 15,
                        justifyContent: 'center',
                    }}
                >
                    {atendimentoItems.map((item) => (
                        <div
                            key={item.value}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '.5rem',
                            }}
                        >
                            <span
                                style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: item.color,
                                }}
                            />

                            {item.value}
                        </div>
                    ))}
                </div>
            </div>

            {/* Mensagens */}
            <div
                style={{
                    paddingLeft: '30px',
                    borderLeft: '1px solid #E2E2E2',
                    textAlign: 'center',
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{ marginBottom: '.5rem', textAlign: 'left' }}
                >
                    Mensagens Enviadas
                </Typography>

                <div
                    style={{
                        display: 'flex',
                        gap: 15,
                        justifyContent: 'center',
                    }}
                >
                    {messageItems.map((item) => (
                        <div
                            key={item.value}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '.5rem',
                            }}
                        >
                            <span
                                style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: item.color,
                                }}
                            />

                            {item.value}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default class Dialog360UsageChart extends PureComponent {

    static demoUrl = 'https://codesandbox.io/s/simple-line-chart-kec3v';

    render() {
        return (
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={this.props.data}
                    margin={{
                        top    : 5,
                        right  : 30,
                        left   : 20,
                        bottom : 5,
                    }}
                >
                    <CartesianGrid stroke="#E2E2E2" vertical={false} />
                    <XAxis dataKey="name"/>
                    <YAxis/>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend content={<CustomLegend />} />
                    {/*<ReferenceLine y="1000" stroke="black" strokeWidth={3}
                        label={{ 
                            position: 'top'
                        }}
                    />*/}
                    <Line dataKey="Total"        stroke="#343A40" strokeWidth={2} />
                    <Line dataKey="Ativo"        stroke="#1976D2" strokeWidth={2} />
                    <Line dataKey="Receptivo"    stroke="#2E7D32" strokeWidth={2} />
                    <Line dataKey="Serviço"      stroke="#00A896" strokeWidth={2} />
                    <Line dataKey="Marketing"    stroke="#D32F2F" strokeWidth={2} />
                    <Line dataKey="Autenticação" stroke="#7B1FA2" strokeWidth={2} />
                    <Line dataKey="Utilidade"    stroke="#EF6C00" strokeWidth={2} />
                </LineChart>
            </ResponsiveContainer>
        );
    }
}
